import { pickLocalized, type EventCard as Card, type LatLng, type Locale } from '@spot/shared';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { eventPath } from '../../app/paths';
import { pressable } from '../../components/ui/pressable';
import { CATEGORY_TEXT, isCategorySlug } from '../map/categories';
import { CategoryIcon } from '../map/category-icon';
import { distanceKm, timeBadge } from '../map/peek-format';
import { RatingBadge } from '../reviews/rating-badge';

/** One row in the Activities lists: time badge, title, area · distance, seats. */
export function EventCard({
	event,
	timeZone,
	locale,
	viewer,
	now,
}: {
	event: Card;
	timeZone: string;
	locale: Locale;
	viewer: LatLng | null;
	now: Date;
}) {
	const { t } = useTranslation();
	const km = distanceKm(viewer, event.placement.point);
	const place = [
		event.landmark === null
			? null
			: t('map.peek.near_metro', { name: pickLocalized(event.landmark.name, locale) }),
		km === null ? null : t('map.peek.distance_km', { km }),
	]
		.filter((part): part is string => part !== null)
		.join(' · ');

	return (
		<Link
			to={eventPath(event.id)}
			state={{ entry: 'list' }}
			className={`bg-surface border-border rounded-card flex gap-3 border p-3 ${pressable}`}
		>
			<span
				className={`bg-bg flex size-12 shrink-0 items-center justify-center rounded-full ${
					isCategorySlug(event.category) ? CATEGORY_TEXT[event.category] : 'text-text'
				}`}
			>
				<CategoryIcon slug={event.category} className="size-6" />
			</span>
			<span className="flex min-w-0 flex-1 flex-col gap-0.5">
				<span className="text-caption text-primary font-semibold">
					{timeBadge(new Date(event.startAt), {
						now,
						timeZone,
						locale,
						today: t('map.peek.today'),
						tomorrow: t('map.peek.tomorrow'),
					})}
					{event.status === 'cancelled' && (
						<span className="text-danger"> · {t('event.cancelled')}</span>
					)}
				</span>
				<span className="text-body text-text truncate font-semibold">{event.title}</span>
				{place !== '' && <span className="text-caption text-text-muted truncate">{place}</span>}
			</span>
			<span className="text-caption text-text-muted flex shrink-0 flex-col items-end justify-center gap-0.5">
				{event.seats.taken}/{event.seats.max}
				{/* A past event's own average (T-905); null while below 3 reviews. */}
				<RatingBadge rating={event.rating} />
			</span>
		</Link>
	);
}
